import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck, MoreHorizontal } from "lucide-react";

/**
 * DashboardShell — member console chrome: a sticky sidebar rail on desktop,
 * a scrollable pill strip on tablets and a fixed bottom bar on phones.
 * Mirrors the admin console's information architecture so the two surfaces
 * feel like one product.
 */
export function DashboardShell({ sections, active, onSelect, children, title, subtitle, actions }) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAr = i18n.language === "ar";
  const [moreOpen, setMoreOpen] = useState(false);

  const primary = sections.slice(0, 4);
  const activeSection = sections.find((s) => s.id === active) || sections[0];

  // Sections that carry a `to` are routes of their own; the rest are panels
  // owned by whichever page renders the shell.
  const pick = (id) => {
    const section = sections.find((s) => s.id === id);
    setMoreOpen(false);
    if (section?.to) {
      navigate(section.to);
    } else if (onSelect) {
      onSelect(id);
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const externals = user?.role === "admin"
    ? [{ to: "/admin", label: t("header.adminPanel", { defaultValue: "Admin" }), icon: ShieldCheck, testid: "member-nav-admin" }]
    : [];

  return (
    <div className="mx-auto max-w-[1400px] px-3 sm:px-6 lg:px-8 py-4 sm:py-8 pb-24 sm:pb-10" data-testid="dashboard-shell">
      <div className="flex gap-6">
        {/* Desktop rail */}
        <aside className="hidden lg:flex flex-col w-[236px] shrink-0 sticky top-24 self-start" data-testid="member-sidebar">
          <nav className="flex flex-col gap-1 rounded-2xl border border-border bg-card/70 backdrop-blur p-2.5">
            {sections.map((s) => {
              const Icon = s.icon;
              const isActive = s.id === active;
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => pick(s.id)}
                  className={`group relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors duration-200 ${
                    isActive ? "bg-primary/12 text-primary" : "text-muted-foreground hover:bg-accent hover:text-foreground"
                  }`}
                  aria-current={isActive ? "page" : undefined}
                  data-testid={`member-nav-${s.id}`}
                >
                  <span className={`absolute inset-y-2 start-0 w-[3px] rounded-full transition-opacity ${isActive ? "bg-primary opacity-100" : "opacity-0"}`} />
                  <Icon className="h-[18px] w-[18px] shrink-0" />
                  <span className="truncate">{s.label}</span>
                  {s.badge > 0 && (
                    <span className="ms-auto font-mono text-[10px] font-semibold rounded-full bg-primary text-primary-foreground px-1.5 py-0.5" data-testid={`member-nav-badge-${s.id}`}>
                      {s.badge > 99 ? "99+" : s.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>

          {externals.length > 0 && (
          <div className="mt-3 rounded-2xl border border-border bg-card/50 p-2.5 space-y-0.5">
            {externals.map((e) => {
              const Icon = e.icon;
              return (
                <Link
                  key={e.to}
                  to={e.to}
                  className="flex items-center gap-3 rounded-xl px-3 py-2 text-xs font-medium text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
                  data-testid={e.testid}
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  <span className="truncate">{e.label}</span>
                </Link>
              );
            })}
          </div>
          )}
        </aside>

        {/* Content column */}
        <div className="min-w-0 flex-1">
          {/* Tablet / small-desktop pill strip */}
          <div className="lg:hidden mb-4 sm:mb-6 -mx-3 sm:mx-0 px-3 sm:px-0">
            <div className="flex gap-1.5 overflow-x-auto [&::-webkit-scrollbar]:hidden [scrollbar-width:none]" data-testid="member-nav-strip">
              {sections.map((s) => {
                const Icon = s.icon;
                const isActive = s.id === active;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => pick(s.id)}
                    className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-2 text-xs font-semibold border transition-all duration-200 ${
                      isActive
                        ? "bg-primary text-primary-foreground border-primary shadow-[0_4px_14px_rgba(0,0,0,0.12)]"
                        : "bg-background text-muted-foreground border-border hover:border-primary/40 hover:text-primary"
                    }`}
                    aria-current={isActive ? "page" : undefined}
                    data-testid={`member-nav-pill-${s.id}`}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {s.label}
                    {s.badge > 0 && <span className="font-mono opacity-80">{s.badge > 99 ? "99+" : s.badge}</span>}
                  </button>
                );
              })}
            </div>
          </div>

          {title ? (
            <div className="mb-4 sm:mb-6 flex flex-wrap items-end justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-widest text-muted-foreground">
                  {activeSection?.label}
                </p>
                <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight mt-1" data-testid="member-page-title">
                  {title}
                </h1>
                {subtitle && <p className="text-sm text-muted-foreground mt-1.5 max-w-2xl">{subtitle}</p>}
              </div>
              {actions}
            </div>
          ) : (
            <h2 className="sr-only">{activeSection?.label}</h2>
          )}
          {children}
        </div>
      </div>

      {/* Phone bottom bar */}
      <nav
        className="sm:hidden fixed bottom-0 inset-x-0 z-40 border-t-2 border-border bg-background/[0.98] backdrop-blur pb-[env(safe-area-inset-bottom,0px)] shadow-[0_-8px_24px_rgba(15,23,42,0.08)]"
        aria-label="Dashboard"
        data-testid="member-bottom-nav"
      >
        <div className="grid grid-cols-5">
          {primary.map((s) => {
            const Icon = s.icon;
            const isActive = s.id === active;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => pick(s.id)}
                className={`relative flex flex-col items-center justify-center gap-1 py-2.5 min-h-[62px] text-[10px] font-semibold transition-all active:scale-95 ${
                  isActive ? "text-primary" : "text-muted-foreground"
                }`}
                aria-current={isActive ? "page" : undefined}
                data-testid={`member-bottom-nav-${s.id}`}
              >
                <span className={`relative flex h-9 w-9 items-center justify-center rounded-2xl transition-all ${isActive ? "bg-primary text-primary-foreground" : "bg-muted/40"}`}>
                  <Icon className="h-5 w-5" />
                  {s.badge > 0 && (
                    <span className="absolute -top-0.5 -end-0.5 min-w-[15px] rounded-full bg-rose-500 px-1 text-[9px] font-semibold leading-[15px] text-white">
                      {s.badge > 9 ? "9+" : s.badge}
                    </span>
                  )}
                </span>
                <span className="truncate max-w-[64px] leading-tight">{s.label}</span>
              </button>
            );
          })}

          <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
            <SheetTrigger asChild>
              <button
                type="button"
                className="flex flex-col items-center justify-center gap-1 py-2.5 min-h-[62px] text-[10px] font-semibold text-muted-foreground active:scale-95"
                data-testid="member-bottom-nav-more"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-2xl bg-muted/40">
                  <MoreHorizontal className="h-5 w-5" />
                </span>
                <span>{t("memberNav.more", { defaultValue: "More" })}</span>
              </button>
            </SheetTrigger>
            <SheetContent side={isAr ? "right" : "left"} aria-describedby={undefined} className="w-[280px] max-w-[86vw] p-4 overflow-y-auto" data-testid="member-more-panel">
              <SheetTitle className="font-display text-sm font-semibold pt-6 pb-3 border-b border-border mb-3">
                {t("memberNav.title", { defaultValue: "My workspace" })}
              </SheetTitle>
              <div className="flex flex-col gap-0.5">
                {sections.map((s) => {
                  const Icon = s.icon;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => pick(s.id)}
                      className={`flex items-center gap-3 text-start rounded-lg px-3 py-2.5 text-sm transition-colors ${
                        s.id === active ? "bg-primary/12 text-primary font-medium" : "text-muted-foreground hover:bg-accent"
                      }`}
                      aria-current={s.id === active ? "page" : undefined}
                      data-testid={`member-more-nav-${s.id}`}
                    >
                      <Icon className="h-4 w-4" /> {s.label}
                      {s.badge > 0 && <span className="ms-auto font-mono text-[10px]">{s.badge}</span>}
                    </button>
                  );
                })}
              </div>
              <div className="mt-4 pt-3 border-t border-border flex flex-col gap-1">
                {externals.map((e) => {
                  const Icon = e.icon;
                  return (
                    <Button key={e.to} asChild variant="ghost" size="sm" className="justify-start rounded-lg gap-3 text-xs">
                      <Link to={e.to} data-testid={`${e.testid}-mobile`}>
                        <Icon className="h-4 w-4" /> {e.label}
                      </Link>
                    </Button>
                  );
                })}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </div>
  );
}

export default DashboardShell;
